<script>
import InputLine from '@/components/atoms/inputlib/components/InputLine'
import Box from '@/components/atoms/Box'
import RadioLine from '@/components/molecules/input/RadioLine.vue'
import ImageWrapper from '@/components/atoms/ImageWrapper.vue'
import Modal from '@/components/atoms/Modal.vue'
import ButtonLoader from '@/components/atoms/ButtonLoader.vue'
import ExpenseCodeBlock from '@/components/organism/checkout/ExpenseCodeBlock'

export default {
    name: 'CardsBlock',
    components: {
        ButtonLoader,
        Modal,
        ImageWrapper,
        RadioLine,
        Box,
        InputLine,
        ExpenseCodeBlock,
    },
    props: {
        stripe: null,
        value: null,
        profileView: Boolean,
        complementaryPayment: Boolean,
    },
    data() {
        return {
            method: this.$cardPayment,
        }
    },
    computed: {
        locale() {
            return this.$store.getters['locale']
        },
        nullBudgetCanteen() {
            return this.$cart.calculation?.cloudcanteen?.budget === null
        },
        expenseRuleSelected() {
            return !!this.$cart.selected_expense_rule
        },
        cart() {
            return this.$cart
        },
        selectedCard: {
            get() {
                if (this.complementaryPayment)
                    return this.expenseRuleSelected || this.$cart.isCloudCanteen
                        ? this.stripe.selectedCard
                        : null
                else
                    return this.expenseRuleSelected ||
                        (this.$cart.isCloudCanteen && !this.nullBudgetCanteen)
                        ? null
                        : this.stripe.selectedCard
            },
            set(card) {
                this.$cart.selected_card = card
                this.stripe.selectedCard = card
            },
        },
        selectedOnlinePayment: {
            get() {
                if (this.complementaryPayment)
                    return this.expenseRuleSelected || this.$cart.isCloudCanteen
                        ? this.stripe.selectedOnlinePayment
                        : null
                else
                    return this.expenseRuleSelected ||
                        (this.$cart.isCloudCanteen && !this.nullBudgetCanteen)
                        ? null
                        : this.stripe.selectedOnlinePayment
            },
            set(card) {
                this.$cart.selected_online_payment = card
                this.stripe.selectedOnlinePayment = card
            },
        },
        showExpenseCodes() {
            return (
                !this.complementaryPayment &&
                this.$cart.payment_method === this.$cardPayment &&
                !this.expenseRuleSelected &&
                !this.$cart.isCloudCanteen
            )
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (
                    value !== this.$cardPayment &&
                    this.$cardPayment !== this.$cart.payment_method
                ) {
                    this.stripe.off(this.placeOrder)
                    this.stripe.selectedCard = null
                    this.stripe.selectedOnlinePayment = null
                }
            },
        },
        showExpenseCodes: {
            immediate: true,
            handler(value) {
                if (!value) {
                    this.$refs.expenseCodeBlock?.clear()
                }
            },
        },
    },
    methods: {
        onInput() {
            this.$emit('input', this.method)
        },
        async open() {
            this.$refs.addCardModal.openModal()
            this.$nextTick(() => {
                this.stripe.initCard()
            })
        },
        close() {
            this.$refs.addCardModal.closeModal()
            this.stripe.resetCard()
        },
        async saveCard() {
            await this.stripe.saveCard(this.$cart.delivery_name)
            await this.stripe.fetchCardsList()
            this.$refs.addCardModal.closeModal()
            this.stripe.resetCard()
        },
        async deleteCard(id) {
            await this.stripe.deleteCard(id)
            await this.stripe.fetchCardsList()
        },
        selectCard() {
            this.selectedOnlinePayment = null
            this.stripe.resetOnlinePaymentButton()
            this.stripe.off(this.placeOrder)
            this.onInput()
        },
        selectOnlinePaymnet() {
            this.selectedCard = null
            this.stripe.initOnlinePaymentButton()
            this.stripe.on(this.placeOrder)
            this.onInput()
        },
        getOnlinePaymentIcon(onlinePayment) {
            return [
                'fab',
                onlinePayment
                    ?.trim()
                    ?.split(/(?=[A-Z])/)
                    ?.map((element) => element?.trim()?.toLowerCase())
                    ?.join('-'),
            ]
        },
        async placeOrder($event) {
            try {
                await this.$store.dispatch('checkout/placeOrder', $event)
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Error during placing the order', e)
            }
        },
    },
}
</script>

<template lang="pug">
.cards-block
    template(v-if="profileView")
        .d-flex.justify-content-between.align-items-center.mb-3
            h2.my-0 {{ $t('profile.payment_methods') }}
            button.btn.btn-primary.btn-xs(@click="open")
                icon.mr-2(icon="fa-solid fa-plus")
                | {{$t('order.stripe.add_card')}}
        .cards-wrapper(:key="`cardsBlock-${!!stripe.savedCards.length}`")
            .card.mb-5(
                v-if="stripe.savedCards.length"
                v-for="(card, index) in stripe.savedCards"
                :key="`savedCard-${index}-${stripe.savedCards.length}`")
                .card-body.d-flex.align-items-center.p-5
                    image-wrapper.mr-5(:src="`/images/cards/stp_card_${card.brand}.png`" height="30px")
                    .d-flex.flex-column
                        span {{ $t(`order.stripe.card_types.${card.brand}`, {cardNumber: card.last}) }}
                        span.text-73 {{ $t('order.stripe.card_ending', {cardNumber: card.last}) }}
                    button-loader.btn.btn-transparent.btn-icon.ml-auto(@click="deleteCard(card.id)" :loading="stripe.loadingDelete === card.id")
                        icon(icon="fa-solid fa-trash")
        
    template(v-else)
        radio-line(
            v-for="(onlinePayment, index) in stripe.avaiableOnlinePaymnet"
            v-model="selectedOnlinePayment"
            @input="selectOnlinePaymnet"
            :class="{'hover-darker': complementaryPayment, 'card': !complementaryPayment, 'active': selectOnlinePaymnet === onlinePayment && !complementaryPayment}"
            :data="onlinePayment"
            :reverse="complementaryPayment"
            :key="`onlinePayment-${index}`")
            .d-flex.align-items-center
                .online-payment-icon
                    icon(:icon="getOnlinePaymentIcon(onlinePayment)" size="xl")
                | {{$t(`order.stripe.${onlinePayment}`)}}
        .cards-wrapper(:key="`cardsBlock-${!!stripe.savedCards.length}`")
            radio-line(
                v-for="(card, index) in stripe.savedCards"
                v-model="selectedCard"
                @input="selectCard"
                :class="{'hover-darker': complementaryPayment, 'card': !complementaryPayment, 'active': card && selectedCard === card.id && !complementaryPayment}"
                :data="card.id"
                :reverse="complementaryPayment"
                :key="`savedCard-${index}`")
                .d-flex.align-items-center
                    image-wrapper(:src="`/images/cards/stp_card_${card.brand}.png`" height="30px")
                    | {{ $t('order.stripe.card_ending', {cardNumber: card.last}) }}
            .radio-line-wrapper(:class="{'hover-darker': complementaryPayment,'card px-3': !complementaryPayment}")
                box(v-if="!complementaryPayment" :value="showExpenseCodes" slim)
                    expense-code-block.mt-4(id="expense_code" ref="expenseCodeBlock" :active="showExpenseCodes")                    
                button.btn.btn-add.py-5.px-3(@click="open")
                    icon.mr-2(icon="fa-solid fa-plus")
                    | {{$t('order.stripe.add_card')}}
    modal.add-card-modal(ref="addCardModal" :closable="false")
        template(v-slot:header)
            .modal-header-text {{$t('order.stripe.add_card')}}
        template(v-slot:body)
            transition(name="fade")
                .row.add-new-card.pb-5
                    .col-12
                        .form-group
                            label {{$t('order.stripe.card_number')}}
                            .form-control(:class="{'is-invalid': stripe.cardErrors[stripe.CARD], 'form-loading': !stripe.cardLoaded}")
                                image-wrapper.mr-2(:src="`/images/cards/stp_card_${stripe.cardErrors[stripe.CARD] ? 'error' : stripe.brand}.png`" height="20px")
                                #card-number
                    .col-6
                        .form-group
                            label {{$t('order.stripe.expires')}}
                            .form-control(:class="{'is-invalid': stripe.cardErrors[stripe.EXPIRY], 'form-loading': !stripe.cardLoaded}")
                                #card-expiry
                                transition(name="fade")
                                    image-wrapper.ml-2(v-if="stripe.cardErrors[stripe.EXPIRY]" :src="`/images/cards/stp_card_error.png`" height="20px")
                    .col-6
                        .form-group
                            label {{$t('order.stripe.security_code')}}
                            .form-control(:class="{'is-invalid': stripe.cardErrors[stripe.CVC], 'form-loading': !stripe.cardLoaded}")
                                #card-cvc
                                image-wrapper.ml-2(:src="`/images/cards/${stripe.cardErrors[stripe.CVC] ? 'stp_card_error' : 'stp_card_cvc'}.png`" height="20px")
                    transition(name="fade")
                        .position-absolute.text-danger.bottom-0.pl-5(v-if="stripe.cardErrorMessag") {{stripe.cardErrorMessage}}
        template(v-slot:footer)
            transition(name="fade")
                .text-danger.mr-auto(v-if="stripe.error") {{stripe.error}}
            button.btn.btn-secondary(@click="close" :disabled="stripe.loadingSave") {{$t('base.cancel')}}
            button-loader.btn.btn-primary(@click="saveCard" :disabled="!stripe.cardComplete || stripe.loadingSave || stripe.cardError" :loading="stripe.loadingSave" inline) {{$t('base.save')}}

</template>
